import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  ListItemButton,
} from '@mui/material';
import { GlassBackdrop, GlassSurface } from '../../components/GlassDashboard';
import {
  People as PersonnelIcon,
  AccessTime as PointageIcon,
  Description as DocumentIcon,
  AccountBalance as PaieIcon,
  Assessment as SyntheseIcon,
  Construction as DevIcon,
  CheckCircle as DoneIcon,
  ChevronRight as ChevronRightIcon,
  EventBusy as CongeIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  PersonAdd as NewHireIcon,
} from '@mui/icons-material';
import { hrDashboardAPI, HRDashboardStats } from '../../services/api';

// HR Module color
const HR_COLOR = '#00897B';

const HROverview: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<HRDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await hrDashboardAPI.getStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching HR stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Calculate totals for pending requests
  const totalPendingRequests = (stats?.conges?.demandes_en_attente || 0) + (stats?.prets?.demandes_en_attente || 0);

  const statsCards = [
    {
      title: 'Effectif Actif',
      value: loading ? '-' : (stats?.effectif?.actifs?.toString() || '0'),
      icon: <PersonnelIcon sx={{ fontSize: 40 }} />,
      color: HR_COLOR,
      description: stats?.effectif?.nouveaux_ce_mois
        ? `+${stats.effectif.nouveaux_ce_mois} ce mois`
        : 'Total des employés actifs',
      path: '/hr/personnel/fiches',
    },
    {
      title: 'Présence Aujourd\'hui',
      value: loading ? '-' : (stats?.presence?.presents_aujourdhui?.toString() || '0'),
      icon: <PointageIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      description: `Absents: ${stats?.presence?.absents_aujourdhui || 0} | En congé: ${stats?.presence?.en_conge_aujourdhui || 0}`,
      path: '/hr/personnel/pointage',
    },
    {
      title: 'Demandes en Attente',
      value: loading ? '-' : totalPendingRequests.toString(),
      icon: <DocumentIcon sx={{ fontSize: 40 }} />,
      color: totalPendingRequests > 0 ? '#FF9800' : '#4CAF50',
      description: `Congés: ${stats?.conges?.demandes_en_attente || 0} | Prêts: ${stats?.prets?.demandes_en_attente || 0}`,
      path: '/hr/documents/demande-conge',
    },
    {
      title: 'Prêts en Cours',
      value: loading ? '-' : (stats?.prets?.prets_en_cours?.toString() || '0'),
      icon: <MoneyIcon sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
      description: `Restant: ${(stats?.prets?.montant_restant || 0).toLocaleString('fr-FR')} DH`,
      path: '/hr/documents/demande-pret',
    },
  ];

  const modules = [
    {
      title: 'Gestion du Personnel',
      icon: <PersonnelIcon />,
      items: [
        { name: 'Fiche du Personnel', status: 'done', path: '/hr/personnel/fiches' },
        { name: 'Pointage', status: 'done', path: '/hr/personnel/pointage' },
      ],
    },
    {
      title: 'Documentaire',
      icon: <DocumentIcon />,
      items: [
        { name: 'Attestation Travail', status: 'done', path: '/hr/documents/attestation-travail' },
        { name: 'Attestation Salaire', status: 'done', path: '/hr/documents/attestation-salaire' },
        { name: 'Demande Prêt', status: 'done', path: '/hr/documents/demande-pret' },
        { name: 'Demande Acompte', status: 'done', path: '/hr/documents/demande-acompte' },
        { name: 'Demande Congé', status: 'done', path: '/hr/documents/demande-conge' },
        { name: 'Autorisation Absence', status: 'done', path: '/hr/documents/autorisation-absence' },
      ],
    },
    {
      title: 'Gestion Paie',
      icon: <PaieIcon />,
      items: [
        { name: 'Base de Calcul Paie', status: 'dev', path: '/hr/paie/base-calcul' },
        { name: 'Gestion STC', status: 'dev', path: '/hr/paie/stc' },
        { name: 'Configuration Paie', status: 'dev', path: '/hr/paie/configuration' },
      ],
    },
    {
      title: 'Synthèse',
      icon: <SyntheseIcon />,
      items: [
        { name: 'État 9421 (Annuel)', status: 'dev', path: '/hr/synthese/etat-9421' },
        { name: 'Journal Contrôle Comptable', status: 'dev', path: '/hr/synthese/journal-controle' },
        { name: 'Livre de Paie', status: 'dev', path: '/hr/synthese/livre-paie' },
      ],
    },
    {
      title: 'Configuration',
      icon: <DevIcon />,
      items: [
        { name: 'Jours Fériés', status: 'done', path: '/hr/configuration/jours-feries' },
      ],
    },
  ];

  const getStatusChip = (status: string) => {
    if (status === 'done') {
      return (
        <Chip
          label="Terminé"
          size="small"
          sx={{
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            color: '#4CAF50',
            fontSize: '0.7rem',
          }}
        />
      );
    }
    return (
      <Chip
        label="En développement"
        size="small"
        sx={{
          backgroundColor: 'rgba(0, 137, 123, 0.1)',
          color: HR_COLOR,
          fontSize: '0.7rem',
        }}
      />
    );
  };

  const getStatusIcon = (status: string) => {
    if (status === 'done') {
      return <DoneIcon sx={{ fontSize: 18, color: '#4CAF50' }} />;
    }
    return <DevIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />;
  };

  return (
    <GlassBackdrop>
      <Typography variant="h4" gutterBottom sx={{ color: HR_COLOR, fontWeight: 600 }}>
        Tableau de Bord Ressources Humaines
      </Typography>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <GlassSurface
              accentColor={card.color}
              onClick={() => navigate(card.path)}
              sx={{ p: 2 }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {card.title}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: card.color }}>
                    {loading ? <CircularProgress size={24} /> : card.value}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {card.description}
                  </Typography>
                </Box>
                <Box sx={{ color: card.color }}>{card.icon}</Box>
              </Box>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      {/* Secondary Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <GlassSurface accentColor={HR_COLOR} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: HR_COLOR }}>
              {loading ? '-' : `${stats?.presence?.taux_presence_mois || 0}%`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Taux de présence (mois)
            </Typography>
          </GlassSurface>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GlassSurface accentColor="#2196F3" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#2196F3' }}>
              {loading ? '-' : (stats?.conges?.employes_en_conge || 0)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Employés en congé
            </Typography>
          </GlassSurface>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GlassSurface accentColor="#FF9800" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#FF9800' }}>
              {loading ? '-' : (stats?.conges?.jours_pris_ce_mois || 0)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Jours de congé (mois)
            </Typography>
          </GlassSurface>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GlassSurface accentColor="#9C27B0" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#9C27B0' }}>
              {loading ? '-' : `${(stats?.prets?.prelevements_mensuels || 0).toLocaleString('fr-FR')}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Prélèvements/mois (DH)
            </Typography>
          </GlassSurface>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Module Status Section */}
      <Typography variant="h5" gutterBottom sx={{ color: HR_COLOR, fontWeight: 500, mb: 2 }}>
        Modules RH
      </Typography>

      <Grid container spacing={3}>
        {modules.map((module, index) => (
          <Grid item xs={12} md={6} key={index}>
            <GlassSurface accentColor={HR_COLOR} interactive={false} sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box sx={{ color: HR_COLOR, mr: 1 }}>{module.icon}</Box>
                <Typography variant="h6" sx={{ color: HR_COLOR }}>
                  {module.title}
                </Typography>
              </Box>
              <List dense>
                {module.items.map((item, idx) => (
                  item.status === 'done' ? (
                    <ListItemButton
                      key={idx}
                      onClick={() => navigate(item.path)}
                      sx={{
                        borderBottom: idx < module.items.length - 1 ? 1 : 'none',
                        borderColor: 'divider',
                        py: 1,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(76, 175, 80, 0.08)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getStatusIcon(item.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.primary',
                          fontWeight: 500,
                        }}
                      />
                      {getStatusChip(item.status)}
                      <ChevronRightIcon sx={{ color: '#4CAF50', ml: 1 }} />
                    </ListItemButton>
                  ) : (
                    <ListItem
                      key={idx}
                      sx={{
                        borderBottom: idx < module.items.length - 1 ? 1 : 'none',
                        borderColor: 'divider',
                        py: 1,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getStatusIcon(item.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary',
                          fontWeight: 400,
                        }}
                      />
                      {getStatusChip(item.status)}
                    </ListItem>
                  )
                ))}
              </List>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      {/* Info Banner */}
      <GlassSurface accentColor={HR_COLOR} interactive={false} sx={{ mt: 3, p: 3 }}>
        <Box display="flex" alignItems="center">
          <DevIcon sx={{ fontSize: 32, color: HR_COLOR, mr: 2 }} />
          <Box>
            <Typography variant="h6" sx={{ color: HR_COLOR }}>
              Module en cours de développement
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Le module Ressources Humaines est en développement actif. Les fonctionnalités marquées "Terminé" sont opérationnelles.
            </Typography>
          </Box>
        </Box>
      </GlassSurface>
    </GlassBackdrop>
  );
};

export default HROverview;
